import React from 'react'
import PropTypes from 'prop-types'
import { Stack, Typography, TextField, CircularProgress } from '@mui/material'
import { useSmartTranslation } from '../../../../../generic/hooks/useSmartTranslation'
import { SmartSelect } from '../../../../../generic/smartSelect'

/**
 * Form to create or modify an incidence.
 *
 * @param {object} props
 * @param {boolean} props.loading Indicates if the catalog is being loaded.
 * @param {string|object|null} props.error Error to display, if any.
 * @param {string[]} props.incidenceTypeOptions Options for incidence types.
 * @param {string} props.selectedTypeName Selected type.
 * @param {Function} props.onTypeChange Callback when type changes.
 * @param {string[]} props.incidenceOptions Options for incidences of the selected type.
 * @param {string} props.selectedIncidenceName Selected incidence.
 * @param {Function} props.onIncidenceChange Callback when incidence changes.
 * @param {string} props.comment Comment introduced.
 * @param {Function} props.onCommentChange Callback when comment changes.
 * @param {boolean} [props.disableType=false] Disables the type selector.
 * @param {boolean} [props.disableIncidence=false] Disables the incidence selector.
 * @param {boolean} [props.saving=false] Indicates if saving is in progress.
 * @returns {JSX.Element}
 */
const IncidenceForm = ({
  loading,
  error,
  incidenceTypeOptions,
  selectedTypeName,
  onTypeChange,
  incidenceOptions,
  selectedIncidenceName,
  onIncidenceChange,
  comment,
  onCommentChange,
  disableType = false,
  disableIncidence = false,
  saving = false
}) => {
  const { t_ } = useSmartTranslation()

  return (
    <Stack spacing={2} sx={{ minWidth: { xs: '100%', md: 520 }, pt: 2 }}>
      {loading && (
        <Stack direction='row' spacing={1} alignItems='center'>
          <CircularProgress size={18} />
          <Typography variant='body2'>{t_('Loading...')}</Typography>
        </Stack>
      )}

      <SmartSelect
        selectableOptions={incidenceTypeOptions}
        label={t_('Incidence Type')}
        value={selectedTypeName}
        onChange={(v) => onTypeChange(v || '')}
        disabled={saving || loading || disableType}
      />

      <SmartSelect
        selectableOptions={incidenceOptions}
        label={t_('Incidence')}
        value={selectedIncidenceName}
        onChange={(v) => onIncidenceChange(v || '')}
        disabled={!selectedTypeName || saving || loading || disableIncidence}
      />

      <TextField
        label={t_('Comment')}
        fullWidth
        variant='filled'
        value={comment}
        onChange={(e) => onCommentChange(e.target.value)}
        rows={4}
        multiline
        disabled={saving}
      />

      {error && (
        <Typography color='error' variant='body2'>
          {typeof error === 'string' ? error : error.message || t_('An error occurred')}
        </Typography>
      )}
    </Stack>
  )
}

IncidenceForm.propTypes = {
  loading: PropTypes.bool,
  saving: PropTypes.bool,
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  incidenceTypeOptions: PropTypes.arrayOf(PropTypes.string).isRequired,
  selectedTypeName: PropTypes.string,
  onTypeChange: PropTypes.func.isRequired,
  incidenceOptions: PropTypes.arrayOf(PropTypes.string).isRequired,
  selectedIncidenceName: PropTypes.string,
  onIncidenceChange: PropTypes.func.isRequired,
  comment: PropTypes.string,
  onCommentChange: PropTypes.func.isRequired,
  disableType: PropTypes.bool,
  disableIncidence: PropTypes.bool
}

export default IncidenceForm
